import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { inboxApi } from '../api/inbox-api';
import {
  Conversation,
  ConversationMessage,
  ConversationFilter,
  ConversationListResponse,
  InboxStats,
  ConversationStatus,
} from '../types/inbox-types';

interface InboxState {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: ConversationMessage[];
  filters: ConversationFilter;
  total: number;
  page: number;
  totalPages: number;
  countByStatus: Record<string, number>;
  countByChannel: Record<string, number>;
  stats: InboxStats;
  loading: boolean;
  loadingMessages: boolean;
  loadingStats: boolean;
  sending: boolean;
  error: string | null;
}

const initialState: InboxState = {
  conversations: [],
  currentConversation: null,
  messages: [],
  filters: {
    page: 1,
    limit: 20,
    sortBy: 'lastMessageAt',
    sortOrder: 'DESC',
  },
  total: 0,
  page: 1,
  totalPages: 0,
  countByStatus: {},
  countByChannel: {},
  stats: { total: 0, open: 0, pending: 0, closed: 0, archived: 0, unread: 0, byChannel: {} },
  loading: false,
  loadingMessages: false,
  loadingStats: false,
  sending: false,
  error: null,
};

export const fetchConversations = createAsyncThunk(
  'inbox/fetchConversations',
  async (filters: ConversationFilter) => {
    return inboxApi.getConversations(filters);
  },
);

export const fetchConversationDetail = createAsyncThunk(
  'inbox/fetchConversationDetail',
  async (id: string) => {
    return inboxApi.getConversationById(id);
  },
);

export const fetchMessages = createAsyncThunk(
  'inbox/fetchMessages',
  async ({ conversationId, page, limit }: { conversationId: string; page?: number; limit?: number }) => {
    return inboxApi.getMessages(conversationId, page, limit);
  },
);

export const sendMessage = createAsyncThunk(
  'inbox/sendMessage',
  async ({ conversationId, dto }: { conversationId: string; dto: { content: string; contentType?: string; direction?: string } }) => {
    return inboxApi.sendMessage(conversationId, dto);
  },
);

export const updateConversationStatus = createAsyncThunk(
  'inbox/updateStatus',
  async ({ conversationId, status }: { conversationId: string; status: string }) => {
    return inboxApi.updateStatus(conversationId, status);
  },
);

export const markConversationAsRead = createAsyncThunk(
  'inbox/markAsRead',
  async (conversationId: string) => {
    await inboxApi.markAsRead(conversationId);
    return conversationId;
  },
);

export const fetchInboxStats = createAsyncThunk(
  'inbox/fetchStats',
  async () => {
    return inboxApi.getStats();
  },
);

const inboxSlice = createSlice({
  name: 'inbox',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<ConversationFilter>) => {
      state.filters = action.payload;
    },
    setCurrentConversation: (state, action: PayloadAction<Conversation | null>) => {
      state.currentConversation = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    // WebSocket: append new message
    addMessage: (state, action: PayloadAction<ConversationMessage>) => {
      state.messages.push(action.payload);
    },
    // WebSocket: update conversation in list
    updateConversationInList: (state, action: PayloadAction<Conversation>) => {
      const idx = state.conversations.findIndex((c) => c.id === action.payload.id);
      if (idx >= 0) {
        state.conversations[idx] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch conversations
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.conversations;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.countByStatus = action.payload.countByStatus;
        state.countByChannel = action.payload.countByChannel;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar conversaciones';
      });

    // Fetch conversation detail
    builder
      .addCase(fetchConversationDetail.fulfilled, (state, action) => {
        state.currentConversation = action.payload;
      });

    // Fetch messages
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.loadingMessages = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loadingMessages = false;
        state.messages = action.payload.messages;
      })
      .addCase(fetchMessages.rejected, (state) => {
        state.loadingMessages = false;
      });

    // Send message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.sending = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sending = false;
        state.messages.push(action.payload);
      })
      .addCase(sendMessage.rejected, (state) => {
        state.sending = false;
      });

    // Update status
    builder
      .addCase(updateConversationStatus.fulfilled, (state, action) => {
        state.currentConversation = action.payload;
        const idx = state.conversations.findIndex((c) => c.id === action.payload.id);
        if (idx >= 0) {
          state.conversations[idx] = action.payload;
        }
      });

    // Mark as read
    builder
      .addCase(markConversationAsRead.fulfilled, (state, action) => {
        const conversationId = action.payload;
        const idx = state.conversations.findIndex((c) => c.id === conversationId);
        if (idx >= 0) {
          state.conversations[idx].unreadCount = 0;
        }
        if (state.currentConversation?.id === conversationId) {
          state.currentConversation.unreadCount = 0;
        }
      });

    // Stats
    builder
      .addCase(fetchInboxStats.pending, (state) => {
        state.loadingStats = true;
      })
      .addCase(fetchInboxStats.fulfilled, (state, action) => {
        state.loadingStats = false;
        state.stats = action.payload;
      })
      .addCase(fetchInboxStats.rejected, (state) => {
        state.loadingStats = false;
      });
  },
});

export const {
  setFilters,
  setCurrentConversation,
  clearMessages,
  clearError,
  addMessage,
  updateConversationInList,
} = inboxSlice.actions;

export default inboxSlice.reducer;
